import { useEffect, useEffectEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ownsErrorFeedback } from '@d-orca/shared/client/hooks';
import { useToast } from './toastContext';

/**
 * The backstop that keeps a failed write from going unseen: every mutation
 * that fails puts one error toast on the shared stack — unless it declared,
 * through `OWNS_ERROR_FEEDBACK`, that its failure is reported elsewhere (its
 * own toast, an inline error). A write nobody thought to report still says it
 * was not saved; a burst of the same failure reads as one toast (the stack
 * drops a repeat that is still showing).
 *
 * Mounted once, inside the app's `ToastProvider` and query client.
 */
export function MutationErrorBackstop() {
  const { t } = useTranslation();
  const push = useToast();
  const queryClient = useQueryClient();

  const report = useEffectEvent(() =>
    push({ kind: 'error', title: t('common.writeFailed.title'), sub: t('common.writeFailed.sub') }),
  );

  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (event.type !== 'updated' || event.action.type !== 'error') return;
        if (!ownsErrorFeedback(event.mutation.options.meta)) report();
      }),
    [queryClient],
  );

  return null;
}
